import Tooltip from "@/components/Tooltip";
import useUserTimezone from "@/hooks/useUserTimezone";
import { buildUsTimezoneConversionRows, type UsTimezoneConversionRow } from "@/lib/usTimezoneHelpers";
import { formatOrderDateTime } from "./orderPresentation";

interface OrderDateTimeDisplayProps {
  value: string | null | undefined;
  className?: string;
}

function UsTimezoneConversionContent({ rows }: { rows: UsTimezoneConversionRow[] }) {
  return (
    <table className="w-full border-collapse text-body-small">
      <tbody>
        {rows.map((row) => (
          <tr key={row.zone} className="border-t border-sys-brand-outline-variant first:border-t-0">
            <td className="py-1.5 pr-3 align-top font-medium tabular-nums">{row.abbrev}</td>
            <td className="py-1.5 pr-3 align-top text-sys-brand-on-surface-variant">{row.isYours ? "Yours" : ""}</td>
            <td className="py-1.5 pr-3 align-top text-right tabular-nums whitespace-nowrap">{row.time}</td>
            <td className="py-1.5 align-top text-right tabular-nums whitespace-nowrap">{row.date}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function OrderDateTimeDisplay({ value, className }: OrderDateTimeDisplayProps) {
  const userTimezone = useUserTimezone();
  const display = formatOrderDateTime(value);

  if (!value || display === "--") {
    return <span className={className}>{display}</span>;
  }

  const { all: allRows, nearby: nearbyRows } = buildUsTimezoneConversionRows(value, userTimezone);
  if (allRows.length === 0) {
    return <span className={className}>{display}</span>;
  }

  const hasHiddenRows = nearbyRows.length < allRows.length;

  return (
    <Tooltip
      variant="rich"
      color="white"
      title="Time zones"
      positioning="fixed"
      compactHoverArea
      anchorCorner="start-start"
      menuCorner="start-end"
      anchorClassName="inline"
      showExpand={hasHiddenRows}
      supportingText={<UsTimezoneConversionContent rows={nearbyRows} />}
      expandedSupportingText={<UsTimezoneConversionContent rows={allRows} />}
    >
      <span className={className}>{display}</span>
    </Tooltip>
  );
}
